import { useMemo, useState } from 'react';
import { cn } from 'lib/cn';
import { LoadingState, ErrorState, EmptyState } from 'components/feedback';
import { SearchIcon, ImageIcon, BoxIcon } from 'components/icons';
import { formatMoney, humanizeEnum } from 'lib/formatters';

// Cap the number of cards rendered at once so a 1-2k product catalog stays
// snappy; the search + subcategory filters narrow things down past this.
const MAX_RENDER = 120;

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition',
        active
          ? 'bg-brand-600 text-white'
          : 'bg-white text-slate-600 ring-1 ring-inset ring-surface-300 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

/**
 * POS-style product picker: a search bar, subcategory filter chips and a grid of
 * tappable product cards. Calls `onSelect(item)` when a card is tapped.
 * `getBadge(item)` may return a number to show on the card (e.g. cart quantity).
 */
export function ProductGrid({ items, loading, error, onRetry, onSelect, getBadge, isItemDisabled }) {
  const [search, setSearch] = useState('');
  const [subcategory, setSubcategory] = useState('all');

  const sellable = useMemo(() => items.filter((item) => item.vendorCatalogId), [items]);

  const subcategories = useMemo(() => {
    const counts = {};
    sellable.forEach((item) => {
      if (item.subcategory) counts[item.subcategory] = (counts[item.subcategory] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ value, count }));
  }, [sellable]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sellable.filter((item) => {
      if (subcategory !== 'all' && item.subcategory !== subcategory) return false;
      if (!query) return true;
      const haystack = [item.vendorProductName, item.name, item.brand]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [sellable, search, subcategory]);

  const visible = filtered.slice(0, MAX_RENDER);

  if (loading && items.length === 0) return <LoadingState label="Loading products…" />;
  if (error && items.length === 0) return <ErrorState error={error} onRetry={onRetry} />;

  return (
    <div>
      <div className="relative mb-3">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search products"
          aria-label="Search products"
          className="h-11 w-full rounded-lg border border-surface-300 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        />
      </div>

      {subcategories.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          <Chip active={subcategory === 'all'} onClick={() => setSubcategory('all')}>
            All
          </Chip>
          {subcategories.map((entry) => (
            <Chip
              key={entry.value}
              active={subcategory === entry.value}
              onClick={() => setSubcategory(entry.value)}
            >
              {humanizeEnum(entry.value)} ({entry.count})
            </Chip>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={BoxIcon}
          title="No products"
          description="Try a different search or filter."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {visible.map((item) => {
              const badge = getBadge ? getBadge(item) : 0;
              const disabled = isItemDisabled ? isItemDisabled(item) : false;
              const name = item.vendorProductName || item.name || 'Unnamed product';
              const image = item.vendorPrimaryImageUrlThumbnail || item.vendorPrimaryImageUrl;
              return (
                <button
                  key={item.vendorCatalogId}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(item)}
                  className={cn(
                    'panel relative flex h-full flex-col overflow-hidden text-left transition',
                    disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:border-brand-300 hover:shadow-md',
                  )}
                >
                  {badge > 0 && (
                    <span className="absolute right-1.5 top-1.5 z-10 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-brand-600 px-1.5 text-xs font-semibold text-white">
                      {badge}
                    </span>
                  )}
                  {item.stockQuantity != null && Number(item.stockQuantity) <= 0 && (
                    <span className="absolute left-1.5 top-1.5 z-10 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                      Out of stock
                    </span>
                  )}
                  <div className="aspect-square w-full shrink-0 overflow-hidden bg-surface-100">
                    {image ? (
                      <img src={image} alt={name} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-slate-300">
                        <ImageIcon className="h-8 w-8" />
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-between gap-1 p-2">
                    <p className="line-clamp-2 min-h-[2rem] text-xs font-medium leading-tight text-slate-900">
                      {name}
                    </p>
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm font-semibold text-slate-900">
                        {formatMoney(item.vendorPrice)}
                      </span>
                      {item.stockQuantity != null && (
                        <span className="shrink-0 text-[10px] text-slate-400">
                          {item.stockQuantity} left
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {filtered.length > MAX_RENDER && (
            <p className="mt-3 text-center text-xs text-slate-400">
              Showing {MAX_RENDER} of {filtered.length}. Refine your search to see more.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default ProductGrid;
